import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Terminal, Copy, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

type Props = {
  config: Record<string, any>;
  onComplete: () => void;
  onBack: () => void;
};

export function ProvisioningStep({ onComplete, onBack }: Props) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const commands = [
    {
      title: 'Step 1: Mount Dell Installation ISO',
      command: './mount-iso.sh',
      description: 'Attach the Ubuntu Server ISO to the target system via iDRAC',
    },
    {
      title: 'Step 2: Initialize Terraform',
      command: 'terraform init',
      description: 'Download required Terraform providers',
    },
    {
      title: 'Step 3: Review Terraform Plan',
      command: 'terraform plan',
      description: 'Preview infrastructure changes before applying',
    },
    {
      title: 'Step 4: Apply Infrastructure',
      command: 'terraform apply -auto-approve',
      description: 'Provision the server infrastructure (may take 15-20 minutes)',
    },
  ];

  const copyCommand = async (command: string, index: number) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast({ title: 'Copied to clipboard' });
    } catch (err) {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Infrastructure Provisioning</CardTitle>
          <CardDescription>
            Execute the following commands in sequence from the extracted bundle directory
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {commands.map((cmd, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">{cmd.title}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{cmd.description}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 rounded-md bg-muted font-mono text-sm flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-muted-foreground" />
                  <code>{cmd.command}</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyCommand(cmd.command, index)}
                >
                  {copiedIndex === index ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Alert>
        <AlertDescription className="text-xs">
          Monitor the Terraform output for any errors. The provisioning process includes OS installation, 
          driver setup, and initial network configuration.
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onComplete}>
          Provisioning Complete
        </Button>
      </div>
    </div>
  );
}
